import { Tooltip } from '@radix-ui/themes';
import clsx from 'clsx';
import MutedText from 'components/common/MutedText';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertPopoverFieldEdit from 'components/HubertUI/HubertPopoverFieldEdit';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import type { TFormValuesResult } from 'hooks/useFormValues';
import { useFormValues } from 'hooks/useFormValues';
import { Info } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import type { TFormKeysV4 } from 'types/questions/question.types.v4';
import {
  ECreateQuestionMode,
  useQuestionEditorContext,
} from 'components/Questionsv2/builder/context/create-question.context';
import { useTemplateId } from 'hooks/useTemplate';
import { updateFormField } from 'rtk/Question.Editor.Slice';
import { selectQuestionLanguage, selectQuestionMode } from 'rtk/questionEditorSelector';
import { formParametersUpdateByFormId } from 'rtk/TemplateSlice';
import { EEventType } from 'types/events.enum';
import { type TFormSubType } from '@hubertai/screening-question-types';
import { useTranslation } from 'react-i18next';

type TFormValuesKey = keyof Omit<TFormValuesResult, 'isLoading'>;

export type TFormFieldUpdater = {
  label: string;
  description: string;
  formKey: TFormKeysV4;
  fieldType: 'rich-text' | 'text' | 'textarea' | 'number' | 'boolean';
  formValuesKey: TFormValuesKey;
  form: TFormSubType;
  formIndex: number;
};

type Props = TFormFieldUpdater & ComponentProps<'div'>;

const FormFieldUpdater = ({
  label,
  description,
  formKey,
  fieldType,
  formValuesKey,
  form,
  formIndex,
  ...props
}: Props) => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const templateId = useTemplateId();
  const formValues = useFormValues(form, questionId, form.id);

  const handleUpdate = (value: any) => {
    dispatch(updateFormField({ questionId, formIndex, field: formKey, value, language: questionLanguage }));

    if (templateId && mode === ECreateQuestionMode.InTemplateEdit) {
      dispatch(
        formParametersUpdateByFormId({
          templateId,
          eventType: EEventType.MODIFIED,
          resource: {
            nodeId: questionId,
            formId: form.id,
            key: formKey,
            language: questionLanguage,
            value,
          },
          description: `User updated a form field (${form.id}/${formKey}) in question ${questionId} in template ${templateId} to "${value}".`,
        }),
      );
    }
  };

  return (
    <div
      {...props}
      className={clsx(
        'w-full',
        // 'my-8',
        ['rich-text', 'text', 'text-area'].includes(fieldType)
          ? 'flex flex-col justify-between gap-2'
          : 'flex flex-row justify-between',
      )}
    >
      <div className="hubert-vertical-center flex flex-row gap-2">
        <HubertLabel>{label}</HubertLabel>
        <Tooltip content={description}>
          <Info />
        </Tooltip>
      </div>

      <div className={clsx('hubert-vertical-center')}>
        {(() => {
          if (fieldType === 'rich-text') {
            return (
              <HubertPopoverFieldEdit>
                <HubertPopoverFieldEdit.Trigger tooltipContent={label} asChild containerClass="w-full">
                  <div
                    className="border-primary-50 rounded-md border px-4 py-2"
                    dangerouslySetInnerHTML={{
                      __html: formValues[formValuesKey].value,
                    }}
                    dir={'auto'}
                  />
                </HubertPopoverFieldEdit.Trigger>
                <HubertPopoverFieldEdit.Content
                  editType="rich-text"
                  value={formValues[formValuesKey].value}
                  onFieldCommit={(value: string) => {
                    handleUpdate(value);
                  }}
                >
                  <div>
                    <div className="text-lg font-semibold">{t('form.fieldUpdater.editLabel', { label })}</div>
                    <MutedText text={t('form.fieldUpdater.updateText')} />
                  </div>
                </HubertPopoverFieldEdit.Content>
              </HubertPopoverFieldEdit>
            );
          } else if (fieldType === 'text') {
            return (
              <HubertPopoverFieldEdit>
                <HubertPopoverFieldEdit.Trigger tooltipContent={label} asChild containerClass="w-full">
                  <div
                    className="border-primary-50 rounded-md border px-4 py-2"
                    dir={'auto'}
                    dangerouslySetInnerHTML={{
                      __html: formValues[formValuesKey].value,
                    }}
                  />
                </HubertPopoverFieldEdit.Trigger>
                <HubertPopoverFieldEdit.Content
                  editType="text"
                  value={formValues[formValuesKey].value}
                  onFieldCommit={(value: string) => {
                    handleUpdate(value);
                  }}
                >
                  <div>
                    <div className="text-lg font-semibold">{t('form.fieldUpdater.editLabel', { label })}</div>
                    <MutedText text={t('form.fieldUpdater.updateText')} />
                  </div>
                </HubertPopoverFieldEdit.Content>
              </HubertPopoverFieldEdit>
            );
          } else if (fieldType === 'textarea') {
            return (
              <HubertPopoverFieldEdit>
                <HubertPopoverFieldEdit.Trigger tooltipContent={label} asChild containerClass="w-full">
                  <div
                    className="border-primary-50 rounded-md border px-4 py-2"
                    dir={'auto'}
                    dangerouslySetInnerHTML={{
                      __html: formValues[formValuesKey].value,
                    }}
                  />
                </HubertPopoverFieldEdit.Trigger>
                <HubertPopoverFieldEdit.Content
                  editType="textarea"
                  value={formValues[formValuesKey].value}
                  onFieldCommit={(value: string) => {
                    handleUpdate(value);
                  }}
                >
                  <div>
                    <div className="text-lg font-semibold">{t('form.fieldUpdater.editLabel', { label })}</div>
                    <MutedText text={t('form.fieldUpdater.updateText')} />
                  </div>
                </HubertPopoverFieldEdit.Content>
              </HubertPopoverFieldEdit>
            );
          } else if (fieldType === 'boolean') {
            return (
              <HubertSwitch
                onCheckedChange={(value) => {
                  handleUpdate(value);
                }}
                checked={formValues[formValuesKey].value}
                id={`form-${form.id}-is-${formKey}-${questionLanguage}`}
              />
            );
          }

          if (fieldType === 'number') {
            return (
              <HubertPopoverFieldEdit>
                <HubertPopoverFieldEdit.Trigger tooltipContent={label} asChild containerClass="w-full">
                  <div className="border-primary-100 border px-4 py-2" dir={'auto'}>
                    {formValues[formValuesKey].value}
                  </div>
                </HubertPopoverFieldEdit.Trigger>
                <HubertPopoverFieldEdit.Content
                  editType="number"
                  value={formValues[formValuesKey].value}
                  onFieldCommit={(value: string) => {
                    handleUpdate(parseInt(value));
                  }}
                >
                  <div>
                    <div className="text-lg font-semibold">{t('form.fieldUpdater.editLabel', { label })}</div>
                    <MutedText text={t('form.fieldUpdater.updateNumber')} />
                  </div>
                </HubertPopoverFieldEdit.Content>
              </HubertPopoverFieldEdit>
            );
          } else {
            return <div>{t('form.fieldUpdater.wrongType')}</div>;
          }
        })()}
      </div>
    </div>
  );
};

export default FormFieldUpdater;
